import { chain } from 'lodash';
import { updateReferencesListForPreviewMode } from '~/utils/footnotes';

export const stripCitationOverlayContainer = (contentString) => {
  const citationOverlayRgx = new RegExp(
    `<div\\b[^>]*?id="citation-bullets-overlay"[^>]*?>(?:(?!<\/div>)<[^<]*)*<\/div>`,
    'gmi',
  );
  return contentString.replace(citationOverlayRgx, '');
};

const computePositionBasedOnNeighbors = (top, left, bulletPositions) => {
  // Calculate initial position
  let bulletTop = top - 4;
  let bulletLeft = left;

  // Check for overlaps and adjust position
  const bulletSize = 24; // Fixed bullet size
  const minDistance = bulletSize; // Minimum distance between bullets

  let attempts = 0;
  const maxAttempts = 8; // Try 8 different positions around the original

  while (attempts < maxAttempts) {
    let hasOverlap = false;

    // Check against existing bullet positions
    for (const existingPos of bulletPositions) {
      const distance = Math.sqrt(Math.pow(bulletTop - existingPos.top, 2) + Math.pow(bulletLeft - existingPos.left, 2));

      if (distance < minDistance) {
        hasOverlap = true;
        break;
      }
    }

    if (!hasOverlap) {
      break; // No overlap found, use this position
    }

    // Try different offset positions around the original
    const offset = Math.floor(attempts / 2) + 1;
    const direction = attempts % 2 === 0 ? 1 : -1;

    if (attempts < 4) {
      // Try horizontal offsets first
      bulletLeft = left + offset * direction * 30;
    } else {
      // Then try vertical offsets
      bulletTop = top - 4 + offset * direction * 30;
      bulletLeft = left; // Reset to original left position
    }

    attempts++;
  }
  return { bulletTop, bulletLeft };
};

// Transform doc-reference spans to superscript citations in preview mode
export const transformDocReferences = (htmlContent, footnotes) => {
  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = htmlContent;

  removeCitationBulletsOverlay({ container: tempContainer });

  const CITATION_SEL = 'span[data-doc-id]:not(.transformed)';
  const isCitationEl = (el) => el?.nodeType === 1 && el.matches?.(CITATION_SEL);
  // matches spaces and . , (including non-breaking spaces). These are allowed to sit between citations and still count as “consecutive”.
  const isSkippableText = (n) =>
    n?.nodeType === Node.TEXT_NODE && /^[\s.,\u00A0\u202F\u2009]+$/.test(n.nodeValue || '');

  // 2) Compress consecutive numbers into ranges
  const compress = (nums) =>
    chain(nums)
      .uniq()
      .sortBy()
      .transform((acc, n) => {
        const last = acc[acc.length - 1];
        if (!last || n !== last[1] + 1) acc.push([n, n]);
        else last[1] = n;
      }, [])
      .map(([s, e]) => (s === e ? `${s}` : e === s + 1 ? `${s},${e}` : `${s}–${e}`))
      .value();

  const nextGroup = (start) => {
    const group = [start];
    const between = [];
    let buf = [];
    let p = start.nextSibling;

    while (p) {
      if (isCitationEl(p)) {
        group.push(p);
        between.push(...buf);
        buf = [];
        p = p.nextSibling;
        continue;
      }
      if (isSkippableText(p)) {
        buf.push(p);
        p = p.nextSibling;
        continue;
      }
      break; // hit real content → stop; keep trailing buf
    }
    return { group, between };
  };

  const dict = {};
  let next = 1;
  Array.from(tempContainer.querySelectorAll('span[data-doc-id]')).forEach((s) => {
    if (!isCitationBulletVisibleOnDoc(s)) return; // skip hidden citations
    const id = s.getAttribute('data-doc-id');
    if (id && !(id in dict)) dict[id] = next++;
  });

  for (let cur; (cur = tempContainer.querySelector(CITATION_SEL)); ) {
    if (!cur.parentNode) break;

    const { group, between } = nextGroup(cur);

    // unique ids for attribute; numbers from dict; compressed text
    const ids = [...new Set(group.map((s) => s.getAttribute('data-doc-id')))];
    const nums = ids.map((id) => dict[id]);

    const sup = document.createElement('sup');
    sup.textContent = compress(nums).join(',');
    sup.setAttribute('data-doc-ids', ids.join(','));

    const first = group[0];
    first.classList.add('transformed');
    first.parentNode.replaceChild(sup, first);

    group.slice(1).forEach((n) => n.remove()); // remove extra spans
    if (group.length > 1) between.forEach((n) => n.remove()); // remove only BETWEEN punctuation
  }

  // 4) Update references once
  tempContainer.querySelectorAll('[data-ant-field="reference-list"]').forEach(child => {
    const textElement = child.querySelector('p');
    updateReferencesListForPreviewMode(textElement, dict, footnotes);
  });

  return tempContainer.innerHTML;
};

export const removeCitationBulletsOverlay = ({ iframeDoc, container }) => {
  let existingOverlay = iframeDoc?.getElementById('citation-bullets-overlay');
  if (container) {
    existingOverlay = container.querySelector('#citation-bullets-overlay');
  }
  if (existingOverlay) {
    existingOverlay.remove();
  }
};

export const renderCitationBulletsOverIframe = async ({
  iframeDoc,
  mapCitationsDataFromFootnote,
  openDocument,
  saveEditorContentStateFromIframe,
  refreshReferencesOnDelete,
  updateEditorSelectableElementsData,
}) => {
  if (!iframeDoc) return;

  removeCitationBulletsOverlay({ iframeDoc });

  const overlay = iframeDoc.createElement('div');
  overlay.id = 'citation-bullets-overlay';
  Object.assign(overlay.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '9999',
  });
  iframeDoc.body.appendChild(overlay);

  // Inject Google Fonts and styles
  const fontLink = iframeDoc.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=DM+Sans&display=swap';
  iframeDoc.head.appendChild(fontLink);

  // Inject styles
  const styleTag = iframeDoc.createElement('style');
  styleTag.innerHTML = tagStyle;
  iframeDoc.head.appendChild(styleTag);

  const spans = Array.from(iframeDoc.querySelectorAll('span[data-doc-id]'));

  // Step 1: Precompute all bullet info
  const dict = {};
  let label = 1;

  const bulletInfos = await Promise.all(
    spans.map(async (span) => {
      const rect = span.getBoundingClientRect();
      const scrollTop = iframeDoc.documentElement.scrollTop || iframeDoc.body.scrollTop;
      const scrollLeft = iframeDoc.documentElement.scrollLeft || iframeDoc.body.scrollLeft;

      const top = rect.top + scrollTop;
      const left = rect.left + scrollLeft;

      // top is 0 only when element is hidden from layout
      if (top === 0) {
        return;
      }
      // calculate bullet label
      const id = span.getAttribute('data-doc-id');
      const snippetIndex = span.getAttribute('data-index');
      let pageData = span.getAttribute('data-page');
      const dictKey = `${id}-${pageData}`;
      if (!dict[dictKey]) dict[dictKey] = label++;
      span.setAttribute('data-label', dict[dictKey]);

      let snippetData = span.getAttribute('data-snippet');
      let updatedByUser = span.getAttribute('data-updated-by-user') === 'true';

      const citationData = await mapCitationsDataFromFootnote(id, pageData, snippetData, snippetIndex);

      if (!citationData) {
        const spanToRemove = iframeDoc.querySelector(`span[data-doc-id="${id}"]`);
        if (spanToRemove) {
          spanToRemove.remove(); // Remove the span from the DOM
        }
        return;
      }

      const { thumbnailUrl, page, name, snippet, isCustomReference } = citationData;

      return {
        id,
        page,
        label: dict[dictKey],
        top,
        left,
        name,
        snippetData: snippet,
        thumbnailUrl,
        updatedByUser,
        isCustomReference,
        span, // Keep reference to the original span for removal
      };
    }),
  );

  // Step 2: Batch append to DOM with collision detection
  const bulletElements = [];
  const tooltipElements = [];
  const bulletPositions = []; // Track positions to avoid overlaps

  bulletInfos.forEach((data) => {
    if (!data) {
      return;
    }

    const { id, page, label, top, left, name, snippetData, thumbnailUrl, updatedByUser, isCustomReference, span } =
      data;

    const bullet = iframeDoc.createElement('div');
    bullet.className = 'info-bullet-wrapper';
    bullet.textContent = label;

    const { bulletTop, bulletLeft } = computePositionBasedOnNeighbors(top, left, bulletPositions);

    // Store the final position
    bulletPositions.push({ top: bulletTop, left: bulletLeft });

    Object.assign(bullet.style, {
      top: `${bulletTop}px`,
      left: `${bulletLeft}px`,
      color: updatedByUser ? '#4CAF50' : '#6222e3',
      border: `2px solid ${updatedByUser ? '#4CAF50' : '#6222e3'}`,
    });

    const tooltip = iframeDoc.createElement('div');
    tooltip.className = 'tooltip-popup';
    tooltip.innerHTML = `
      <div class="tooltip-header">
        ${thumbnailUrl ? `<img class="tooltip-image" src="${thumbnailUrl}" alt="thumb"/>` : ''}
        <div class="tooltip-text">
          <div class="tooltip-title">
            <a href="https://google.com/" target="_blank">${name}</a>
          </div>
          <div class="tooltip-subtext">Page ${page}</div>
        </div>
        <div class="remove-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path d="M7.5 7.5C7.77614 7.5 8 7.72386 8 8V10C8 10.2761 7.77614 10.5 7.5 10.5C7.22386 10.5 7 10.2761 7 10V8C7 7.72386 7.22386 7.5 7.5 7.5Z" fill="#C64444"/>
            <path d="M10 8C10 7.72386 9.77614 7.5 9.5 7.5C9.22386 7.5 9 7.72386 9 8V10C9 10.2761 9.22386 10.5 9.5 10.5C9.77614 10.5 10 10.2761 10 10V8Z" fill="#C64444"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M5.71429 4.5V3.1C5.71429 2.66935 5.8922 2.26039 6.20229 1.96194C6.51172 1.66411 6.92747 1.5 7.35714 1.5H9.64286C10.0725 1.5 10.4883 1.66411 10.7977 1.96194C11.1078 2.26039 11.2857 2.66935 11.2857 3.1V4.5H13.5C13.7761 4.5 14 4.72386 14 5C14 5.27614 13.7761 5.5 13.5 5.5H13V11.9C13 12.3306 12.8221 12.7396 12.512 13.0381C12.2026 13.3359 11.7868 13.5 11.3571 13.5H5.64286C5.21318 13.5 4.79743 13.3359 4.488 13.0381C4.17792 12.7396 4 12.3306 4 11.9V5.5H3.5C3.22386 5.5 3 5.27614 3 5C3 4.72386 3.22386 4.5 3.5 4.5H5.71429ZM6.89576 2.68243C7.01498 2.56768 7.18061 2.5 7.35714 2.5H9.64286C9.81939 2.5 9.98502 2.56768 10.1042 2.68243C10.2228 2.79655 10.2857 2.94717 10.2857 3.1V4.5H6.71429V3.1C6.71429 2.94717 6.77718 2.79655 6.89576 2.68243ZM5 5.5V11.9C5 12.0528 5.0629 12.2034 5.18147 12.3176C5.30069 12.4323 5.46633 12.5 5.64286 12.5H11.3571C11.5337 12.5 11.6993 12.4323 11.8185 12.3176C11.9371 12.2034 12 12.0528 12 11.9V5.5H5Z" fill="#C64444"/>
          </svg>
        </div>
      </div>
      <div class="tooltip-snippet">${snippetData || ''}</div>
    `;

    let tooltipTop = top - 160;
    let tooltipLeft = left + 25;
    const tooltipWidth = 300;
    const viewportWidth = iframeDoc.documentElement.clientWidth;
    if (tooltipTop < 0) tooltipTop = top + 10;
    if (tooltipLeft + tooltipWidth > viewportWidth) tooltipLeft = left - tooltipWidth;

    Object.assign(tooltip.style, {
      top: `${tooltipTop + 80}px`,
      left: `${tooltipLeft}px`,
    });

    let hideTimeout;
    const showTooltip = () => {
      clearTimeout(hideTimeout);
      tooltip.classList.add('visible');
    };
    const hideTooltip = () => {
      hideTimeout = setTimeout(() => tooltip.classList.remove('visible'), 200);
    };

    //click events
    if (!isCustomReference) {
      bullet.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
      tooltip.addEventListener('click', () => {
        openDocument({ reference: { externalId: id }, page });
      });
    }

    //hover events
    bullet.addEventListener('mouseenter', showTooltip);
    bullet.addEventListener('mouseleave', hideTooltip);
    tooltip.addEventListener('mouseenter', showTooltip);
    tooltip.addEventListener('mouseleave', hideTooltip);

    // Add functionality to remove the span
    const removeIcon = tooltip.querySelector('.remove-icon');
    removeIcon.addEventListener('click', (event) => {
      event.stopPropagation(); // Prevent triggering other click events
      const spanToRemove = span;
      if (spanToRemove) {
        spanToRemove.remove(); // Remove the span from the DOM
        bullet.remove(); // Remove the bullet
        tooltip.remove(); // Remove the tooltip
        updateEditorSelectableElementsData();

        setTimeout(() => {
          refreshReferencesOnDelete();
          saveEditorContentStateFromIframe();
        }, 2000);
      }
    });

    bulletElements.push(bullet);
    tooltipElements.push(tooltip);
  });

  // Append all at once to prevent layout thrashing
  bulletElements.forEach((el) => overlay.appendChild(el));
  tooltipElements.forEach((el) => overlay.appendChild(el));
};

const tagStyle = `
    * {
      font-family: 'DM Sans', sans-serif !important;
    }
    .info-bullet-wrapper {
      position: absolute;
      width: 24px;
      height: 24px;
      background-color: #fff;
      border: 2px solid #6222e3;
      color: #6222e3;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
      cursor: pointer;
      z-index: 10000;
      pointer-events: auto;
    }
    .tooltip-popup {
      position: absolute;
      width: 300px;
      background-color: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.2);
      padding: 0;
      display: none;
      flex-direction: column;
      z-index: 10001;
      pointer-events: auto;
    }
    .tooltip-popup.visible {
      display: flex;
    }
    .tooltip-header {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      background-color: #e8e7e5;
      padding: 12px 12px 4px 12px;
      margin-bottom: -2px;
    }
    .tooltip-image {
      width: 60px;
      height: 48px;
      background-color: #ccc;
      margin-right: 8px;
    }
    .tooltip-text {
      flex-grow: 1;
    }
    .tooltip-title {
      font-weight: bold;
      color: #6222e3;
      font-size: 13px;
      margin-bottom: 4px;
      cursor: pointer;
      max-height: 130px;
      max-width: 200px;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      a {
        text-decoration: none !important;
      }
    }
    .tooltip-subtext {
      line-height: 16px;
      font-weight: 500;
      font-style: normal;
      color: #8e8d8b;
      letter-spacing: .1px;
      font-size: 13px;
    }
    .tooltip-snippet {
      font-size: 12px;
      line-height: 1.5;
      padding: 12px;
      color: #333;
      max-height: 46px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }
    .remove-icon {
      position: absolute;
      right: 4px;
      top: 4px;
      font-size: 12px;
      cursor: pointer;
      z-index: 10001;
    }
  `;

export const isCitationBulletVisibleOnDoc = (el) => {
  let node = el;

  while (node) {
    if (node.hasAttribute && node.hasAttribute('hidden')) {
      return false;
    }

    if (node.getAttribute) {
      const style = node.getAttribute('style') || '';
      if (/display\s*:\s*none/.test(style) || /visibility\s*:\s*hidden/.test(style)) {
        return false;
      }
    }

    node = node.parentElement;
  }

  return true;
};
